import {
  ActionItem,
  SearchInput,
  SeparatorLine,
} from '@/components_v2/ContextMenu';
import {
  ITEM_MARGIN,
  OPTION_ITEM_HEIGHT,
} from '@/components_v2/ContextMenu/constants';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FixedSizeList as List, ListOnItemsRenderedProps } from 'react-window';

import {
  CHECK_ALL_KEY,
  LOAD_MORE_THRESHOLD,
  PAGINATED_LISTS_MAX_HEIGHT,
  PAGINATED_SELECTED_MAX_ITEMS,
} from './data';
import { MultiSelectionFilterRenderProps } from './interfaces';
import { Container } from './styles';
import { renderIcon } from './utils';

const Main: React.FC<MultiSelectionFilterRenderProps> = ({
  onAdd,
  onRemove,
  options,
  selectedOptions,
  type,
  onSearch,
  isMobile,
  isPaginated,
  onLoadMore,
}) => {
  const { t } = useTranslation();

  const itemSize = OPTION_ITEM_HEIGHT + ITEM_MARGIN;
  const selectedHeight =
    Math.min(
      selectedOptions.length,
      isPaginated ? PAGINATED_SELECTED_MAX_ITEMS : selectedOptions.length,
    ) * itemSize;
  const optionsHeight = isPaginated
    ? Math.min(
        options.length * itemSize,
        PAGINATED_LISTS_MAX_HEIGHT - selectedHeight,
      )
    : options.length * itemSize;
  const listStyle = { overflow: isPaginated ? 'auto' : 'hidden' };

  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }: ListOnItemsRenderedProps) => {
      if (
        onLoadMore &&
        visibleStopIndex >= options.length - LOAD_MORE_THRESHOLD
      ) {
        onLoadMore();
      }
    },
    [onLoadMore, options.length],
  );

  const renderSelecteds = useCallback(
    ({ index, style }) => {
      const { id, label } = selectedOptions[index];
      const text = id === CHECK_ALL_KEY ? t('filter.all.deselect') : label;
      return (
        <div id={id} style={style}>
          <ActionItem
            key={id}
            onClick={() => onRemove(id)}
            icon={renderIcon(type, true)}
            text={text}
          />
        </div>
      );
    },
    [type, onRemove, selectedOptions, t],
  );

  const renderOptions = useCallback(
    ({ index, style }) => {
      const { id, label } = options[index];
      const checkAllText = isPaginated
        ? t('filter.all.select_loaded')
        : t('filter.all.select');
      const text = id === CHECK_ALL_KEY ? checkAllText : label;
      return (
        <div key={id} style={style}>
          <ActionItem
            onClick={() => onAdd(id)}
            icon={renderIcon(type, false)}
            text={text}
          />
        </div>
      );
    },
    [type, options, onAdd, t, isPaginated],
  );

  return (
    <Container isMobile={isMobile}>
      <SearchInput onSubmit={onSearch} />
      <List
        width="100%"
        style={listStyle}
        height={selectedHeight}
        itemSize={itemSize}
        itemCount={selectedOptions.length}
      >
        {renderSelecteds}
      </List>
      {selectedOptions.length > 0 && options.length > 0 && <SeparatorLine />}
      <List
        width="100%"
        style={listStyle}
        height={optionsHeight}
        itemSize={itemSize}
        itemCount={options.length}
        onItemsRendered={isPaginated ? handleItemsRendered : undefined}
      >
        {renderOptions}
      </List>
    </Container>
  );
};

export default Main;
